import Raphael, { RaphaelPaper, RaphaelElement } from 'raphael';
import React, { useRef, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Gear } from 'core/models/drive';

function flatten<T extends unknown[]>(arrayLike: T[]) {
    return arrayLike.reduce((a, e) => [...a, ...e], [] as unknown[]);
}

function arcPath(cx: number, cy: number, r: number, angle: number, offsetAngle: number) {
    const alpha = Math.min(angle, 359.99);
    const startPosition = offsetAngle;
    const a = (startPosition - alpha) * Math.PI / 180;
    const b = startPosition * Math.PI / 180;
    const sx = cx + r * Math.cos(b);
    const sy = cy - r * Math.sin(b);
    const x = cx + r * Math.cos(a);
    const y = cy - r * Math.sin(a);

    return [['M', sx, sy], ['A', r, r, 0, +(alpha > 180), 1, x, y]];
}

function drawStatus(paper: RaphaelPaper, text: string) {
    const [cx, cy] = [paper.width / 2, paper.height / 2];
    return paper
        .text(cx, cy, text)
        .attr({
            font: '24px Arial',
            fill: '#fff'
        })
        .toFront();
}

function drawBackgroundCircle(paper: RaphaelPaper) {
    const [cx, cy] = [paper.width / 2, paper.height / 2];
    const r = Math.min(cx, cy) * 0.3;

    paper
        .circle(cx, cy, r)
        .attr({ stroke: 'none', fill: '#193340' });
}

function arcPathForBrake(cx: number, cy: number, ratio: number) {
    const r = Math.min(cx, cy) * (0.6 - 0.12);
    return arcPath(cx, cy, r, ratio * 0.93 * 360, -90);
}

function drawBrakeCircle(paper: RaphaelPaper, ratio: number, opacity = 1.0) {
    const [cx, cy] = [paper.width / 2, paper.height / 2];
    const w = Math.min(cx, cy) * 0.24;

    return paper
        .path()
        .attr({
            path: flatten(arcPathForBrake(cx, cy, ratio)).join(),
            stroke: '#d9534f',
            'stroke-opacity': opacity,
            'stroke-width': w,
        });
}

function arcPathForAccel(cx: number, cy: number, ratio: number) {
    const r = Math.min(cx, cy) * (0.9 - 0.12);
    return arcPath(cx, cy, r, ratio * 0.93 * 360, -90);
}

function drawAccelCircle(paper: RaphaelPaper, ratio: number, opacity = 1.0) {
    const [cx, cy] = [paper.width / 2, paper.height / 2];
    const w = Math.min(cx, cy) * 0.24;

    return paper
        .path()
        .attr({
            path: flatten(arcPathForAccel(cx, cy, ratio)).join(),
            stroke: '#5bc0de',
            'stroke-opacity': opacity,
            'stroke-width': w,
        });
}

function drawBrakeCircleGauge(paper: RaphaelPaper) {
    return drawBrakeCircle(paper, 1.0, 0.5);
}

function drawAccelCircleGauge(paper: RaphaelPaper) {
    return drawAccelCircle(paper, 1.0, 0.5);
}

type SVG = {
    cx: number;
    cy: number;
    label: RaphaelElement;
    brakeArc: RaphaelElement;
    accelArc: RaphaelElement;
}

function makeThickStroke(arc: RaphaelElement, w: number) {
    arc.animate({ 'stroke-width': w * 2, opacity: .75 }, 1000, 'elastic');
}

function makeThinStroke(arc: RaphaelElement, w: number) {
    arc.stop().animate({ 'stroke-width': w, opacity: 1 }, 1000, 'elastic');
}

function setText(label: RaphaelElement, text: string, speed: number) {
    label
        .stop()
        .animate({ opacity: 0 }, speed, '>', () => {
            label
                .attr({ text })
                .animate({ opacity: 1 }, speed, '<');
        });
}

function renderSpeedMeter(element: HTMLElement, status: () => SpeedMeterValues) {
    const paper = Raphael(element, element.clientWidth, element.clientHeight);

    const [cx, cy] = [paper.width / 2, paper.height / 2];
    const w = Math.min(cx, cy) * 0.24;

    drawBackgroundCircle(paper);
    drawBrakeCircleGauge(paper);
    drawAccelCircleGauge(paper);

    const defaultText = '';

    const brakeArc = drawBrakeCircle(paper, 0);
    const accelArc = drawAccelCircle(paper, 0);

    const label = drawStatus(paper, defaultText);

    brakeArc
        .mouseover(() => {
            makeThickStroke(brakeArc, w);
            const { brake, currentSpeed } = status();
            setText(label, `Brake Stroke\n${Math.round(brake * 100)}%`, currentSpeed);
        })
        .mouseout(() => {
            makeThinStroke(brakeArc, w);
            const { currentSpeed } = status();
            setText(label, defaultText, currentSpeed);
        });

    accelArc
        .mouseover(() => {
            makeThickStroke(accelArc, w);
            const { accel, currentSpeed } = status();
            setText(label, `Accel Stroke\n${Math.round(accel * 100)}%`, currentSpeed);
        })
        .mouseout(() => {
            makeThinStroke(accelArc, w);
            const { currentSpeed } = status();
            setText(label, defaultText, currentSpeed);
        });

    return { cx, cy, label, brakeArc, accelArc };
}

function updateSpeedMeter(svg: SVG, gear: string, speed: number, accel: number, brake: number) {
    const { cx, cy, label, brakeArc, accelArc } = svg;
    const w = Math.min(cx, cy) * 0.24;

    const text = `- ${gear} -\n${Math.round(speed)} km/h`;
    label
        .attr({ text })
        .animate({ opacity: 1 }, 0, '<');

    brakeArc
        .animate({
            path: flatten(arcPathForBrake(cx, cy, brake)).join(),
            'stroke-width': w,
            opacity: 1
        }, 0, 'elastic');

    accelArc
        .animate({
            path: flatten(arcPathForAccel(cx, cy, accel)).join(),
            'stroke-width': w,
            opacity: 1
        }, 0, 'elastic');
}

const Frame = styled.div({
    height: '200px',
    width: '200px',
});

export type SpeedMeterValues = {
    gear: Gear;
    currentSpeed: number;
    accel: number;
    brake: number;
}

export type SpeedMeterProps = {
    className?: string;
    values: SpeedMeterValues;
}

export const SpeedMeter = (props: SpeedMeterProps) => {
    const divRef = useRef<HTMLDivElement>(null);
    const [svg, setSVG] = useState<SVG>();

    const statusRef = useRef<SpeedMeterValues>(props.values);

    const status = props.values;

    useEffect(() => {
        if (!divRef.current) return;
        const svg = renderSpeedMeter(divRef.current, () => statusRef.current);
        setSVG(svg);
    }, []);

    useEffect(() => {
        statusRef.current = status;
    }, [status]);

    useEffect(() => {
        if (!svg) return;
        updateSpeedMeter(svg, status.gear, status.currentSpeed, status.accel, status.brake);
    }, [status.accel, status.brake, status.currentSpeed, status.gear, svg]);

    return (
        <Frame ref={divRef} className={props.className} />
    );
};
